import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useWhitelistingPausedRedirect } from "@hooks/useWhitelistingPausedRedirect";
import { useWhitelistingSuspended } from "@hooks/useWhitelistingSuspended";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import { closeCart, resetCheckout } from "@store/reducers/cart";

import BillingView from "./BillingView";
import CartView from "./CartView";
import CheckoutView from "./CheckoutView";
import PaymentView from "./PaymentView";
import PlanConfigView from "./PlanConfigView";
import ReviewView from "./ReviewView";
import { CloseBtn, ModalContainer, Overlay, SlidePanel, SliderTrack, SliderViewport } from "./styles";
import SubPaymentView from "./SubPaymentView";
import SubReviewView from "./SubReviewView";
import TipView from "./TipView";
import { STEP_INDEX } from "./types";
import UpgradePaymentView from "./UpgradePaymentView";
import UpgradeReviewView from "./UpgradeReviewView";

const SUB_STEP_INDEX: Record<string, number> = {
  checkout: 0,
  "plan-config": 1,
  "sub-review": 2,
  "sub-payment": 3,
};

const UPGRADE_STEP_INDEX: Record<string, number> = {
  "upgrade-review": 0,
  "upgrade-payment": 1,
};

const CartModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, checkoutStep, isLoggedInCheckout, mode, items, paymentInFlight } = useAppSelector((state) => state.cart);
  const user = useAppSelector((state) => state.user);
  const isAuthenticated = !!(user as any)?.user?.email;
  const shouldSkipBilling = isAuthenticated || isLoggedInCheckout;
  const [skipBilling, setSkipBilling] = useState(shouldSkipBilling);
  const prevCheckoutStep = useRef(checkoutStep);
  const isSubscription = mode === "subscription";
  const isUpgrade = mode === "subscription-upgrade";
  const whitelistingSuspended = useWhitelistingSuspended();
  const goFixWhitelisting = useWhitelistingPausedRedirect();

  // Paused account (ToU §17.4): no purchase flow may be entered at all — close
  // the cart and send them to the Account Issues modal. Guarding HERE rather
  // than per-button is what makes it reliable:
  //   - it covers all three tracks (single licence, subscription, upgrade) and
  //     every step, not just CartView's "Proceed to Checkout" — the subscription
  //     and upgrade tracks never render CartView at all;
  //   - it covers every entry point (licence modal, pricing, account page, a
  //     restored server-side cart), so no new CTA can quietly bypass it;
  //   - as an effect it survives the flag arriving LATE. `useWhitelistingSuspended`
  //     resolves over the network, so a click-time check can be raced: the cart
  //     opens with the flag still false and a fast click walks into checkout.
  // Reactivation deliberately still works — the hook reports false in the
  // awaiting-reactivation state, which is exactly when the checkout must open.
  useEffect(() => {
    if (!isOpen || !whitelistingSuspended) return;
    dispatch(closeCart());
    goFixWhitelisting();
  }, [isOpen, whitelistingSuspended, dispatch, goFixWhitelisting]);

  useEffect(() => {
    if (checkoutStep !== prevCheckoutStep.current) {
      setSkipBilling(shouldSkipBilling);
      prevCheckoutStep.current = checkoutStep;
    }
  }, [checkoutStep, shouldSkipBilling]);

  // Safety net for LICENSE MODE ONLY: if cart opens in license mode with 0
  // items but checkoutStep has drifted past "cart" (stale from a previous
  // shopping session), snap back to the canonical empty-cart view.
  //
  // Subscription / subscription-upgrade modes are EXEMPT — they legitimately
  // operate with items=[] because subs use `state.subscription` config, not
  // `state.items`. Earlier this effect also fired on `mode !== "license"`,
  // which destroyed every `openSubscriptionCheckout` dispatch immediately
  // (mode flipped to subscription, then this effect resetCheckout'd back to
  // license — user clicked "Get Started" on /account/subscription and saw
  // an empty license cart instead of the plan-config panel). Surfaced
  // during Phase 11 Flow 4 on dev. The cross-mode safety net was a
  // belt-and-suspenders for the cart-on-dev investigation; toggleCart
  // (header cart icon click) already resets mode + step on every open, so
  // the cross-mode reset isn't actually load-bearing.
  useEffect(() => {
    if (!isOpen) return;
    if (mode === "license" && items.length === 0 && checkoutStep !== "cart") {
      dispatch(resetCheckout());
    }
  }, [isOpen, mode, items.length, checkoutStep, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setSkipBilling(shouldSkipBilling);
    }
  }, [isOpen, shouldSkipBilling]);

  const stepIndex = isUpgrade
    ? (UPGRADE_STEP_INDEX[checkoutStep] ?? 0)
    : isSubscription
      ? (SUB_STEP_INDEX[checkoutStep] ?? 0)
      : skipBilling
        ? ({ cart: 0, checkout: 1, billing: 2, tip: 2, review: 3, payment: 4 }[checkoutStep] ?? 0)
        : (STEP_INDEX[checkoutStep] ?? 0);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const observerRef = useRef<ResizeObserver | null>(null);
  // While the body is being scrolled (incl. the boundary rubber-band), freeze
  // re-measuring so the modal never animates its height mid-scroll.
  const scrollFreezeRef = useRef(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useLayoutEffect: measure synchronously BEFORE the browser paints, so the
  // first frame shows the correct measured height rather than starting at
  // content-natural height (the CSS `transition: height 0.4s ease` can't
  // animate from auto → pixel, so without this the viewport would snap
  // visibly mid-animation on every step transition).
  useLayoutEffect(() => {
    if (!isOpen) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // A step change must always re-measure (don't inherit a scroll freeze).
    scrollFreezeRef.current = false;

    const contentEl = contentRefs.current[stepIndex];
    if (!contentEl) return;

    // Re-query body/footer on every measure — the footer is conditionally
    // rendered (hidden during the in-step "switch account" / login mode of
    // the Checkout step), so a captured ref goes stale when it unmounts and
    // a new node mounts. Re-querying each call keeps the height correct
    // when DOM nodes come and go inside the same step.
    const measure = () => {
      // Never resize the modal while the user is scrolling the body — that's
      // what caused the height to "auto-adjust" / wobble at the scroll edges.
      if (scrollFreezeRef.current) return;
      const header = contentEl.querySelector("[data-slide-header]") as HTMLElement | null;
      const body = contentEl.querySelector("[data-slide-body]") as HTMLElement | null;
      const footer = contentEl.querySelector("[data-slide-footer]") as HTMLElement | null;

      // The body uses `flex: 1` so its `scrollHeight` never reads below its
      // current `clientHeight`. Instead of mutating the body style (which
      // triggers visible layout flicker), compute natural content height
      // from the bounding rects of the first and last children plus body
      // padding. Margins on the first/last child sit outside their bounding
      // rects, so include them via getComputedStyle to avoid under-counting
      // (which would surface as a phantom scrollbar after the height shrinks).
      let bodyH = 0;
      if (body && body.children.length > 0) {
        const firstChild = body.children[0] as HTMLElement;
        const lastChild = body.children[body.children.length - 1] as HTMLElement;
        const firstRect = firstChild.getBoundingClientRect();
        const lastRect = lastChild.getBoundingClientRect();
        const contentRange = lastRect.bottom - firstRect.top;
        const bodyStyles = getComputedStyle(body);
        const firstStyles = getComputedStyle(firstChild);
        const lastStyles = getComputedStyle(lastChild);
        const padTop = parseFloat(bodyStyles.paddingTop) || 0;
        const padBottom = parseFloat(bodyStyles.paddingBottom) || 0;
        const firstMarginTop = parseFloat(firstStyles.marginTop) || 0;
        const lastMarginBottom = parseFloat(lastStyles.marginBottom) || 0;
        // Small safety buffer to absorb sub-pixel rounding (otherwise a
        // 0.5px under-measure can flash a scrollbar during the height
        // transition while it's resolving).
        const buffer = 2;
        bodyH = padTop + firstMarginTop + contentRange + lastMarginBottom + padBottom + buffer;
      } else if (body) {
        bodyH = body.scrollHeight;
      } else {
        bodyH = contentEl.scrollHeight;
      }

      const footerH = footer ? footer.offsetHeight : 0;
      const headerH = header ? header.offsetHeight : 0;
      const h = headerH + bodyH + footerH;
      // Match ModalContainer's max-height (95vh) minus a 16px buffer so the
      // measured viewport never exceeds the visible modal box. Body's
      // overflow-y:auto handles anything taller.
      const maxH = window.innerHeight * 0.95 - 16;
      if (h > 0) {
        setViewportHeight(Math.min(h, maxH));
      }

      // Re-attach the ResizeObserver to whatever footer/body exist now —
      // observers don't auto-follow DOM mounts, so each measure refreshes
      // the targets so future resizes still trigger.
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current.observe(contentEl);
        if (header) observerRef.current.observe(header);
        if (body) observerRef.current.observe(body);
        if (footer) observerRef.current.observe(footer);
      }
    };

    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(contentEl);

    // Watch the entire content subtree for mounts/unmounts (e.g. footer
    // appearing/disappearing as login mode toggles).
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(measure);
    });
    mutationObserver.observe(contentEl, { childList: true, subtree: true, attributes: true, characterData: true });

    window.addEventListener("resize", measure);

    // Freeze re-measuring while the body scrolls (capture catches the inner
    // scroll); release ~200ms after the last scroll/rubber-band so genuine
    // content changes still settle to the right height afterwards.
    const onScroll = () => {
      scrollFreezeRef.current = true;
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        scrollFreezeRef.current = false;
      }, 200);
    };
    contentEl.addEventListener("scroll", onScroll, { capture: true, passive: true });

    // Synchronous first measurement before paint — kills the auto→pixel
    // viewport snap on initial open / step change. Subsequent measurements
    // come from the ResizeObserver + MutationObserver as before.
    measure();

    return () => {
      observerRef.current?.disconnect();
      mutationObserver.disconnect();
      contentEl.removeEventListener("scroll", onScroll, { capture: true } as any);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      window.removeEventListener("resize", measure);
    };
  }, [stepIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setViewportHeight(undefined);
        dispatch(resetCheckout());
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen, dispatch]);

  const handleClose = useCallback(() => {
    // A payment is in flight — the card may already be charged and the webhook
    // may be mid-write. Closing here would drop the user on a page that tells
    // them nothing about a charge that is happening. Back is disabled in the
    // payment views for the same reason; this covers the ✕ and Escape.
    if (paymentInFlight) return;
    dispatch(closeCart());
    // Reset checkout step after close animation finishes to prevent swipe-back
    setTimeout(() => {
      dispatch(resetCheckout());
    }, 350);
  }, [dispatch, paymentInFlight]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, handleClose]);

  // Scrollbar stays visible — modal overlay handles click-blocking
  // (removed overflow:hidden to prevent content shift)


  const setContentRef = (index: number) => (el: HTMLDivElement | null) => {
    contentRefs.current[index] = el;
  };

  const refStyle: React.CSSProperties = { display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" };


  return (
    <Overlay $open={isOpen}>
      <ModalContainer $open={isOpen}>
        <CloseBtn
          onClick={handleClose}
          aria-label="Close cart"
          disabled={paymentInFlight}
          title={paymentInFlight ? "Please wait — your payment is being processed" : undefined}
          style={paymentInFlight ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
        >
          &#x2715;
        </CloseBtn>

        <SliderViewport style={viewportHeight ? { height: viewportHeight } : undefined}>
          {isUpgrade ? (
            <SliderTrack $step={stepIndex} $panels={2}>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(0)}><UpgradeReviewView /></div>
              </SlidePanel>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(1)}><UpgradePaymentView /></div>
              </SlidePanel>
            </SliderTrack>
          ) : isSubscription ? (
            <SliderTrack $step={stepIndex} $panels={4}>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(0)}><CheckoutView /></div>
              </SlidePanel>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(1)}><PlanConfigView /></div>
              </SlidePanel>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(2)}><SubReviewView /></div>
              </SlidePanel>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(3)}><SubPaymentView /></div>
              </SlidePanel>
            </SliderTrack>
          ) : (
            <SliderTrack $step={stepIndex} $panels={skipBilling ? 5 : 6}>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(0)}><CartView /></div>
              </SlidePanel>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(1)}><CheckoutView /></div>
              </SlidePanel>
              {!skipBilling && (
                <SlidePanel>
                  <div style={refStyle} ref={setContentRef(2)}><BillingView /></div>
                </SlidePanel>
              )}
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(skipBilling ? 2 : 3)}><TipView /></div>
              </SlidePanel>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(skipBilling ? 3 : 4)}><ReviewView /></div>
              </SlidePanel>
              <SlidePanel>
                <div style={refStyle} ref={setContentRef(skipBilling ? 4 : 5)}><PaymentView /></div>
              </SlidePanel>
            </SliderTrack>
          )}
        </SliderViewport>
      </ModalContainer>
    </Overlay>
  );
};

export default CartModal;
